/**
 * #513 — la cara visible del preview de reparto.
 *
 * Estos tres componentes son lo ÚNICO que las dos pantallas de aprobación
 * (`MaterialRequestsInbox`, `SupervisorDashboard`) montan para mostrar qué va a
 * pasar al aprobar. No hay una versión "del inbox" y otra "del supervisor": esa
 * duplicación es literalmente el bug de #513.
 *
 * El CSS se importa acá adentro a propósito. `MaterialRequestsInbox` no importa
 * NINGÚN stylesheet y `SupervisorDashboard` importa el suyo propio, así que
 * meter estos estilos en `SupervisorDashboard.css` los dejaría sin cargar en el
 * inbox — el modo exacto de falla de "editar un CSS que la pantalla real no
 * importa". Importándolo desde el componente, viaja con él.
 *
 * @module components/materials/MaterialSplitPreview
 */

import React from 'react';
import PropTypes from 'prop-types';
import { Badge, Alert } from 'react-bootstrap';
import {
  SPLIT_KIND,
  SIMPLE_KIND,
  MATCH_LABELS,
  describePlan,
  describeSimpleRequest,
} from '../../utils/materialSplitPreview';
import './MaterialSplitPreview.css';

const MATCH_COLORS = {
  matched: 'secondary',
  fuzzy: 'warning',
  unmatched: 'info',
  user_confirmed_new: 'info',
};

/**
 * El conteo del encabezado. Reemplaza a "N stock / M compra" contado por
 * `fulfillment_type` (que desde #384 no decide nada).
 *
 * Mientras el stock está en vuelo NO se muestran números: un número provisorio
 * que después cambia es la misma mentira más corta.
 */
export function MaterialSplitBadges({ summary, loading }) {
  if (loading) {
    return (
      <span className="msp-badges">
        <Badge bg="light" text="dark" className="msp-badge">Calculando reparto…</Badge>
      </span>
    );
  }

  const counts = summary?.counts || {};
  return (
    <span className="msp-badges">
      {counts.stock > 0 && (
        <Badge bg="success" className="msp-badge" title="Ítems que salen completos de bodega">
          {counts.stock} de bodega
        </Badge>
      )}
      {counts.partial > 0 && (
        <Badge bg="warning" text="dark" className="msp-badge" title="Ítems que se reparten: una parte de bodega y el resto se compra">
          {counts.partial} {counts.partial === 1 ? 'parcial' : 'parciales'}
        </Badge>
      )}
      {counts.purchase > 0 && (
        <Badge bg="primary" className="msp-badge" title="Ítems que se compran completos">
          {counts.purchase} de compra
        </Badge>
      )}
      {counts.unknown > 0 && (
        <Badge bg="dark" className="msp-badge" title="No se pudo leer el stock de estos ítems: el reparto se decide al aprobar">
          {counts.unknown} sin dato
        </Badge>
      )}
      {/* #664 — el hueco de cantidad se ve DESDE LA FILA CERRADA, sin abrir el
          detalle: es lo único de esta tarjeta que impide aprobar, así que quien
          revisa la cola tiene que verlo antes de tocar "Aprobar". */}
      {counts.no_qty > 0 && (
        <Badge bg="danger" className="msp-badge" title="Nadie dijo cuántas unidades: hay que completar la cantidad antes de poder aprobar">
          {counts.no_qty} sin cantidad
        </Badge>
      )}
    </span>
  );
}

MaterialSplitBadges.propTypes = {
  summary: PropTypes.shape({ counts: PropTypes.object }),
  loading: PropTypes.bool,
};

/**
 * Una línea de ítem con su reparto. El texto ("Retirar 60 / comprar 40 unid")
 * es la información que decide la aprobación, así que va a ≥14px y no dentro de
 * un badge diminuto.
 */
export function MaterialSplitItemRow({ plan, loading }) {
  const item = plan?.item || {};
  const kind = plan?.kind || SPLIT_KIND.PURCHASE;

  return (
    <div className="msp-item">
      <div className="msp-item-name">
        <span className="fw-semibold">{item.item_description || item.name || '—'}</span>
        {item.match_status && (
          <Badge bg={MATCH_COLORS[item.match_status] || 'secondary'} className="msp-match-badge">
            {MATCH_LABELS[item.match_status] || item.match_status}
          </Badge>
        )}
      </div>
      <div className={`msp-item-split msp-kind-${kind}`}>
        {loading ? 'Calculando reparto…' : describePlan(plan)}
      </div>
    </div>
  );
}

MaterialSplitItemRow.propTypes = {
  plan: PropTypes.object.isRequired,
  loading: PropTypes.bool,
};

/**
 * El aviso de incertidumbre. Sin esto, "sin dato" en un badge parece una
 * categoría más y no una falla de lectura.
 */
export function MaterialSplitStockWarning({ error }) {
  if (!error) return null;
  return (
    <Alert variant="warning" className="msp-warning py-2">
      <i className="bi bi-exclamation-triangle me-2" />
      No se pudo leer el stock actual ({error.message || 'error desconocido'}). Los ítems
      marcados <strong>sin confirmar</strong> se van a repartir recién al aprobar, con la
      regla vieja (todo de bodega o todo de compra).
    </Alert>
  );
}

MaterialSplitStockWarning.propTypes = {
  error: PropTypes.object,
};

/**
 * Solicitud de UN ítem (cabecera sola). Ver `describeSimpleRequest`: el texto
 * sale de ahí y las dos bandejas montan ESTOS dos componentes — no hay una
 * versión del inbox y otra del supervisor.
 */
const SIMPLE_BADGE = {
  [SIMPLE_KIND.EXTERNAL]: {
    bg: 'primary',
    title: 'Material fuera del catálogo: se compra aparte, aprobar no genera la orden de compra',
  },
  [SIMPLE_KIND.INVENTORY]: {
    bg: 'dark',
    title: 'Ítem del catálogo: sale de bodega al aprobar',
  },
  [SIMPLE_KIND.MISSING]: {
    bg: 'danger',
    title: 'La solicitud no dice qué material es',
  },
};

/** Misma tinta que el reparto multi-ítem: compra / reparto al aprobar / falta dato. */
const SIMPLE_KIND_CLASS = {
  [SIMPLE_KIND.EXTERNAL]: SPLIT_KIND.PURCHASE,
  [SIMPLE_KIND.INVENTORY]: SPLIT_KIND.UNKNOWN,
  [SIMPLE_KIND.MISSING]: SPLIT_KIND.NO_QTY,
};

/** El chip de la fila cerrada: nombra el material, no "1 ítem". */
export function MaterialSimpleBadge({ req }) {
  const d = describeSimpleRequest(req);
  const b = SIMPLE_BADGE[d.kind];
  return (
    <span className="msp-badges">
      <Badge bg={b.bg} className="msp-badge" title={b.title}>{d.label}</Badge>
    </span>
  );
}

MaterialSimpleBadge.propTypes = {
  req: PropTypes.object.isRequired,
};

/** La línea del detalle: qué es, cuánto, y qué pasa al aprobar. */
export function MaterialSimpleItemRow({ req }) {
  const d = describeSimpleRequest(req);
  return (
    // #790 — `--frase`: la consecuencia de una solicitud simple es una ORACION
    // ("Se compra aparte: aprobar no consume stock ni genera orden de compra."),
    // no el veredicto corto de la tabla multi-item ("Retirar 6 / comprar 6").
    // La diferencia no la da el color (`msp-kind-*`): una externa es `purchase`
    // igual que una linea de compra de la tabla. La da QUIEN la escribe, y es
    // aca. Sin esta marca la frase hereda el `nowrap` de la columna derecha y
    // desborda: medido 468px de texto en una columna de 288px a 390px de ancho.
    <div className="msp-item msp-item--frase">
      <div className="msp-item-name">
        <span className="fw-semibold">{d.title}</span>
        {' — '}{d.cantidad}
        {d.supplier && <> · proveedor sugerido: {d.supplier}</>}
      </div>
      <div className={`msp-item-split msp-kind-${SIMPLE_KIND_CLASS[d.kind]}`}>
        {d.consequence}
      </div>
    </div>
  );
}

MaterialSimpleItemRow.propTypes = {
  req: PropTypes.object.isRequired,
};

const MaterialSplitPreview = {
  MaterialSplitBadges,
  MaterialSplitItemRow,
  MaterialSplitStockWarning,
  MaterialSimpleBadge,
  MaterialSimpleItemRow,
};

export default MaterialSplitPreview;
